import React, { useEffect, useRef } from "react"
import axios from "../../http/axios"

const SETTINGS_PAGE_NAME = "SiteSettings"
const SNIPPET_KEY = "ChatWidgetSnippet"
const STORAGE_CACHE_KEY = "webchat_snippet_cache_v1"
const CACHE_TTL_MS = 5 * 60 * 1000

function readCachedSnippet() {
  try {
    const raw = localStorage.getItem(STORAGE_CACHE_KEY)
    if (!raw) return null
    const cached = JSON.parse(raw)
    if (!cached || typeof cached.html !== "string") return null
    if (Date.now() - (cached.savedAt || 0) > CACHE_TTL_MS) return null
    return cached.html
  } catch {
    return null
  }
}

function writeCachedSnippet(html) {
  try {
    localStorage.setItem(
      STORAGE_CACHE_KEY,
      JSON.stringify({ html, savedAt: Date.now() })
    )
  } catch {}
}

async function fetchSnippet() {
  const res = await axios.get(`/lookups/page`, {
    params: { pageName: SETTINGS_PAGE_NAME },
  })
  const value = res?.data?.[SNIPPET_KEY]
  return typeof value === "string" && value.length > 0 ? value : null
}

function mountSnippet(root, html) {
  root.innerHTML = html
  // <script> tags inserted via innerHTML do not execute. Re-create them
  // as live script nodes inside the shadow root so the snippet's IIFE runs.
  // document.currentScript is null for dynamically inserted scripts, so
  // expose the shadow root via a global that the snippet falls back to.
  window.__webchatHostRoot = root
  const scripts = Array.from(root.querySelectorAll("script"))
  scripts.forEach(oldScript => {
    const newScript = document.createElement("script")
    for (const attr of oldScript.attributes) {
      newScript.setAttribute(attr.name, attr.value)
    }
    newScript.textContent = oldScript.textContent
    oldScript.parentNode.replaceChild(newScript, oldScript)
  })
}

const WebChatWidget = () => {
  const hostRef = useRef(null)
  const mountedRef = useRef(false)

  useEffect(() => {
    const host = hostRef.current
    if (!host || mountedRef.current) return undefined
    if (host.shadowRoot) return undefined

    mountedRef.current = true
    const root = host.attachShadow({ mode: "open" })
    let cancelled = false

    const cached = readCachedSnippet()
    if (cached) {
      mountSnippet(root, cached)
    }

    fetchSnippet()
      .then(html => {
        if (cancelled || !html) return
        if (html === cached) return
        // Tear down any previously mounted snippet, then mount the fresh one.
        if (typeof host.__webchatCleanup === "function") {
          try { host.__webchatCleanup() } catch {}
          host.__webchatCleanup = null
        }
        mountSnippet(root, html)
        writeCachedSnippet(html)
      })
      .catch(() => {})

    return () => {
      cancelled = true
      if (typeof host.__webchatCleanup === "function") {
        try { host.__webchatCleanup() } catch {}
      }
    }
  }, [])

  return <div ref={hostRef} aria-hidden="false" />
}

export default WebChatWidget
